<template>
  <div class="customer-detail-container">
    <div class="page-header">
      <div class="page-title-block">
        <h2>客户详情</h2>
        <p class="page-subtitle">聚焦单个农户/门店的种植档案与历史回访</p>
      </div>
      <div class="header-actions" :class="{'mobile-actions': isMobile}">
        <el-button @click="handleBack">
          <i class="el-icon-back"></i> 返回
        </el-button>
        <el-button type="primary" @click="handleEditCustomer">
          <i class="el-icon-edit"></i> 编辑客户
        </el-button>
        <el-button type="success" @click="handleAddVisitRecord">
          <i class="el-icon-plus"></i> 新增回访记录
        </el-button>
        <el-button @click="handleExport">
          <i class="el-icon-download"></i> 导出记录
        </el-button>
        <el-button @click="handleExportImages">
          <i class="el-icon-picture-outline"></i> 导出回访图片
        </el-button>
      </div>
    </div>
    
    <!-- 客户基本信息 -->
    <el-card class="customer-info-card">
      <h3>基本信息</h3>
      <div class="customer-info" :class="{'mobile-info': isMobile}">
        <div class="info-item">
          <label>客户名称:</label>
          <span>{{ customer.name }}</span>
        </div>
        <div class="info-item">
          <label>联系电话:</label>
          <span>{{ customer.phone }}</span>
        </div>
        <div class="info-item">
          <label>地址:</label>
          <span class="address-text">{{ customer.address }}</span>
          <el-button
            v-if="customer && customer.longitude && customer.latitude"
            type="text"
            size="mini"
            class="nav-btn-inline"
            @click="handleNavigateCustomer"
          >
            <i class="el-icon-location-outline"></i> 导航
          </el-button>
        </div>
        <div class="info-item">
          <label>管理者:</label>
          <span>{{ customer.manager.name }}</span>
        </div>
        <div class="info-item">
          <label>备注:</label>
          <span>{{ customer.description || '无' }}</span>
        </div>
      </div>
      
      <!-- 客户位置（图标与点击效果与客户地图一致：大的人员图标 + 点击显示弹窗，弹窗内导航） -->
      <div class="location-section">
        <h4>位置信息</h4>
        <div v-if="hasValidLocation" class="map-container">
          <el-amap
            class="map"
            :center="mapCenter"
            :zoom="15"
            :scroll-wheel-zoom="true"
          >
            <el-amap-marker
              :position="markerPosition"
              :events="{ click: handleMarkerClick }"
            >
              <div
                style="
                  width: 60px;
                  height: 60px;
                  border-radius: 50%;
                  background: rgba(64, 158, 255, 0.22);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  box-shadow: 0 0 14px rgba(64, 158, 255, 0.7);
                "
              >
                <i class="el-icon-user-solid" style="font-size: 34px; color: #ffffff;"></i>
              </div>
            </el-amap-marker>
            <el-amap-info-window
              :position="markerPosition"
              :visible="showMapInfoWindow"
              :events="{ close: handleInfoWindowClose }"
            >
              <div class="detail-info-window-content">
                <h3>{{ customer.name }}</h3>
                <p>电话: {{ customer.phone }}</p>
                <p>地址: {{ customer.address }}</p>
                <div class="detail-info-window-actions">
                  <el-button type="primary" size="mini" @click="handleNavigateCustomer">
                    导航
                  </el-button>
                </div>
              </div>
            </el-amap-info-window>
          </el-amap>
          <div class="map-nav-float" @click="handleNavigateCustomer">
            <i class="el-icon-location-outline"></i>
            <span>导航到这里</span>
          </div>
        </div>
        <div v-else class="no-location">未设置位置信息</div>
      </div>
    </el-card>
    
    <!-- 回访记录列表 -->
    <el-card class="visit-records-card">
      <h3>回访记录</h3>
      <!-- 移动端卡片视图 -->
      <div v-if="isMobile" class="visit-records-mobile">
        <div 
          v-for="record in visitRecords" 
          :key="record.id" 
          class="visit-record-card"
        >
          <div class="record-header">
            <span class="record-id">#{{ record.id }}</span>
            <div class="record-actions">
              <el-button type="primary" size="mini" @click="handleEditVisitRecord(record)">
                编辑
              </el-button>
              <el-button type="danger" size="mini" @click="handleDeleteVisitRecord(record)">
                删除
              </el-button>
              <el-button size="mini" @click="handleViewVisitRecordDetail(record)">
                详情
              </el-button>
            </div>
          </div>
          <div class="record-content">
            <p><strong>用药时间:</strong> {{ formatDate(record.medicineTime) }}</p>
            <p><strong>回访时间:</strong> {{ formatDate(record.visitTime) }}</p>
            <p><strong>用药记录:</strong> {{ record.medicineRecord }}</p>
            <p><strong>用肥记录:</strong> {{ record.fertilizerRecord }}</p>
            <p><strong>效果观察记录:</strong> {{ record.effectObservation }}</p>
            <p><strong>问题备注:</strong> {{ record.problemNotes }}</p>
            <p><strong>记录人:</strong> {{ record.recorder.name }}</p>
            <p><strong>记录时间:</strong> {{ formatDate(record.recordTime) }}</p>
          </div>
        </div>
      </div>
      
      <!-- 桌面端表格视图 -->
      <el-table v-else :data="visitRecords" style="width: 100%">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="medicineTime" label="用药时间" width="150">
          <template slot-scope="scope">{{ formatDate(scope.row.medicineTime) }}</template>
        </el-table-column>
        <el-table-column prop="visitTime" label="回访时间" width="150">
          <template slot-scope="scope">{{ formatDate(scope.row.visitTime) }}</template>
        </el-table-column>
        <el-table-column prop="medicineRecord" label="用药记录" min-width="200"></el-table-column>
        <el-table-column prop="fertilizerRecord" label="用肥记录" min-width="200"></el-table-column>
        <el-table-column prop="effectObservation" label="效果观察记录" min-width="300"></el-table-column>
        <el-table-column prop="problemNotes" label="问题备注" min-width="200"></el-table-column>
        <el-table-column prop="recorder.name" label="记录人" width="120"></el-table-column>
        <el-table-column prop="recordTime" label="记录时间" width="150">
          <template slot-scope="scope">{{ formatDate(scope.row.recordTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="210" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" size="small" @click="handleEditVisitRecord(scope.row)">
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDeleteVisitRecord(scope.row)">
              删除
            </el-button>
            <el-button size="small" @click="handleViewVisitRecordDetail(scope.row)">
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog
      title="已保存到下载目录"
      :visible.sync="exportPathDialogVisible"
      width="92%"
      :modal-append-to-body="true"
    >
      <div style="font-size: 13px; color: #606266; line-height: 1.6;">
        <div style="margin-bottom: 6px;">文件路径（可长按复制/截图）：</div>
        <div
          style="padding: 10px; background: #f5f7fa; border-radius: 8px; word-break: break-all; color: #303133;"
        >
          {{ exportSavedPath || '-' }}
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="mini" @click="copyExportPath" :disabled="!exportSavedPath">复制路径</el-button>
        <el-button type="primary" size="mini" @click="exportPathDialogVisible = false">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'CustomerDetail',
  data() {
    return {
      customer: {}, // 客户信息
      visitRecords: [], // 回访记录列表
      isMobile: false,
      showMapInfoWindow: false, // 地图标记点击后显示的信息弹窗
      exportSaving: false, // App 端导出/保存进行中，避免重复点击导致失败
      exportPathDialogVisible: false,
      exportSavedPath: ''
    }
  },
  computed: {
    hasValidLocation() {
      const c = this.customer
      if (!c || !c.longitude || !c.latitude) return false
      const lng = parseFloat(c.longitude)
      const lat = parseFloat(c.latitude)
      return !Number.isNaN(lng) && !Number.isNaN(lat)
    },
    mapCenter() {
      if (!this.hasValidLocation) return [116.397428, 39.90923]
      return [parseFloat(this.customer.longitude), parseFloat(this.customer.latitude)]
    },
    markerPosition() {
      if (!this.hasValidLocation) return [116.397428, 39.90923]
      return [parseFloat(this.customer.longitude), parseFloat(this.customer.latitude)]
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    this.loadCustomerDetail()
    this.loadVisitRecords()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    
    // 返回上一页或客户列表
    handleBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push('/customers')
      }
    },
    
    // 加载客户详情
    loadCustomerDetail() {
      const customerId = this.$route.params.id
      request.get(`/customers/${customerId}`)
        .then(response => {
          this.customer = response.data || response
        })
        .catch(error => {
          console.error('加载客户详情失败:', error)
          this.$message.error('加载客户详情失败')
        })
    },
    
    // 加载回访记录
    loadVisitRecords() {
      const customerId = this.$route.params.id
      request.get(`/visit-records/customer/${customerId}`)
        .then(response => {
          this.visitRecords = response.data || response
        })
        .catch(error => {
          console.error('加载回访记录失败:', error)
          this.$message.error('加载回访记录失败')
        })
    },
    
    // 格式化日期
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    
    // 编辑客户
    handleEditCustomer() {
      this.$router.push(`/customers/edit/${this.customer.id}`)
    },
    
    // 新增回访记录
    handleAddVisitRecord() {
      this.$router.push(`/visit-records/new?customerId=${this.customer.id}`)
    },
    
    // 编辑回访记录
    handleEditVisitRecord(record) {
      this.$router.push(`/visit-records/edit/${record.id}`)
    },
    
    // 删除回访记录
    handleDeleteVisitRecord(record) {
      this.$confirm(`确定要删除这条回访记录吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        request.delete(`/visit-records/${record.id}`)
          .then(() => {
            this.$message.success('删除回访记录成功')
            this.loadVisitRecords() // 重新加载回访记录
          })
          .catch(error => {
            console.error('删除回访记录失败:', error)
            this.$message.error('删除回访记录失败')
          })
      }).catch(() => {
        // 取消删除
      })
    },

    // 查看回访记录详情
    handleViewVisitRecordDetail(record) {
      this.$router.push(`/visit-records/${record.id}`)
    },
    
    // 导出记录
    handleExport() {
      this.downloadFile(`/api/excel/customer/${this.customer.id}`, `客户回访记录_${this.customer.name}.xlsx`)
    },

    // 导出回访图片ZIP
    handleExportImages() {
      if (!this.customer || !this.customer.id) {
        this.$message.error('客户信息未加载完成，无法导出图片')
        return
      }
      this.downloadFile(
        `/api/visit-records/customer/${this.customer.id}/images/export`,
        `客户回访图片_${this.customer.name || ''}.zip`
      )
    },
    
    // 点击地图标记：显示信息弹窗（与客户地图一致，不再直接导航）
    handleMarkerClick() {
      this.showMapInfoWindow = true
    },
    handleInfoWindowClose() {
      this.showMapInfoWindow = false
    },
    // 导航到客户位置（与客户地图页保持一致逻辑）
    handleNavigateCustomer() {
      const lon = parseFloat(this.customer && this.customer.longitude)
      const lat = parseFloat(this.customer && this.customer.latitude)
      if (!lon || !lat || Number.isNaN(lon) || Number.isNaN(lat)) {
        this.$message.warning('客户坐标无效，无法导航')
        return
      }

      const name = encodeURIComponent((this.customer && this.customer.name) || '目的地')
      const webUrl = `https://uri.amap.com/navigation?to=${lon},${lat},${name}&mode=car&policy=1&src=khgl&coordinate=gaode&callnative=1`

      const ua = navigator.userAgent || ''
      const isMobile = /Android|iPhone|iPad|iPod/i.test(ua)
      if (!isMobile) {
        window.open(webUrl, '_blank', 'noopener')
        return
      }

      const schemeUrl = `amapuri://route/plan/?dlat=${lat}&dlon=${lon}&dname=${name}&t=0`
      const start = Date.now()
      window.location.href = schemeUrl
      setTimeout(() => {
        if (Date.now() - start < 1600) {
          window.location.href = webUrl
        }
      }, 1200)
    },
    
    // 通用文件下载方法
    downloadFile(url, filename) {
      // HBuilder(5+) App：优先使用原生 downloader（更稳定，且支持 header）
      if (window.plus && window.plus.downloader) {
        this.downloadFileByPlus(url, filename)
        return
      }

      // 创建新的axios实例，绕过全局响应拦截器
      const downloadAxios = this.$axios.create({
        baseURL: '',
        responseType: 'blob', // 重要：指定响应类型为二进制数据
        timeout: 30000
      })
      
      // 添加认证token
      const token = localStorage.getItem('token')
      
      downloadAxios.get(url, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      })
      .then(response => {
        this.handleFileDownload(response.data, filename)
      })
      .catch(error => {
        console.error('文件下载失败:', error)
        this.$message.error('文件下载失败')
      })
    },
    
    // 处理文件下载
    handleFileDownload(blob, filename) {
      // 创建下载链接
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      
      // 触发下载
      document.body.appendChild(link)
      link.click()
      
      // 清理
      setTimeout(() => {
        URL.revokeObjectURL(url)
        document.body.removeChild(link)
      }, 100)
    },

    _toast(msg) {
      try {
        if (window.plus && window.plus.nativeUI && window.plus.nativeUI.toast) {
          window.plus.nativeUI.toast(String(msg || ''))
        } else if (this.$message) {
          this.$message(msg)
        }
      } catch (e) {
        // ignore
      }
    },

    // HBuilder App：用 plus.downloader 下载到 _downloads（支持 header，最稳）
    downloadFileByPlus(url, filename) {
      try {
        const token = localStorage.getItem('token')
        const safeName = String(filename || 'export.xlsx').replace(/[\\/:*?"<>|]/g, '_')
        const fullUrl = /^https?:\/\//i.test(url)
          ? url
          : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`

        // 先下载到应用文档目录，避免“自动保存即完成”的体验不明确；下载后再让用户选择打开/保存到目标目录
        const tempTarget = `_doc/exports/${Date.now()}_${safeName}`

        const waiting = window.plus && window.plus.nativeUI && window.plus.nativeUI.showWaiting
          ? window.plus.nativeUI.showWaiting('正在导出...')
          : null

        const task = window.plus.downloader.createDownload(
          fullUrl,
          { filename: tempTarget },
          (download, status) => {
            if (waiting && waiting.close) waiting.close()
            if (status === 200) {
              this.showExportActionSheet(download.filename, safeName)
            } else {
              console.error('导出失败 status=', status, download)
              this._toast(`导出失败（${status}）`)
            }
          }
        )

        if (token && task && task.setRequestHeader) {
          task.setRequestHeader('Authorization', `Bearer ${token}`)
        }

        task.start()
      } catch (e) {
        console.error('App 导出失败:', e)
        this._toast('导出失败')
      }
    },

    showExportActionSheet(tempFilePath, displayName) {
      if (!(window.plus && window.plus.nativeUI)) {
        // 没有 nativeUI：直接尝试打开
        if (window.plus && window.plus.runtime && window.plus.runtime.openFile) {
          window.plus.runtime.openFile(tempFilePath)
        }
        return
      }

      window.plus.nativeUI.actionSheet(
        {
          title: '导出完成',
          cancel: '取消',
          buttons: [
            { title: '直接打开' },
            { title: '保存到下载目录' }
          ]
        },
        (e) => {
          // e.index: 0 取消；1..n 对应 buttons
          if (e.index === 1) {
            if (window.plus && window.plus.runtime && window.plus.runtime.openFile) {
              window.plus.runtime.openFile(tempFilePath)
            }
            return
          }
          if (e.index === 2) this.copyPlusFileToPublicDownloads(tempFilePath, displayName)
        }
      )
    },

    copyPlusFileToPublicDownloads(srcPath, destName) {
      if (this.exportSaving) return
      this.exportSaving = true
      this.copyPlusFile(
        srcPath,
        '_downloads/',
        destName,
        '已保存到下载目录',
        (savedPath) => {
          this.exportSavedPath = savedPath || ''
          this.exportPathDialogVisible = true
        }
      )
    },

    copyPlusFile(srcPath, destDirUrl, destName, successToast, onSaved) {
      try {
        if (!(window.plus && window.plus.io)) return

        window.plus.io.resolveLocalFileSystemURL(
          srcPath,
          (srcEntry) => {
            const finish = () => { this.exportSaving = false }

            const doCopyToDir = (dirEntry) => {
              // 目标已存在时 copyTo 会失败，这里改为“先删后拷贝”实现覆盖保存
              const doCopy = () => {
                srcEntry.copyTo(
                  dirEntry,
                  destName,
                  (newEntry) => {
                    this._toast(successToast || '保存成功')
                    if (newEntry && newEntry.toLocalURL) {
                      console.log('导出文件路径:', newEntry.toLocalURL())
                      onSaved && onSaved(newEntry.toLocalURL())
                    } else {
                      onSaved && onSaved('')
                    }
                    finish()
                  },
                  (err) => {
                    console.error('保存失败(copyTo):', err)
                    this._toast('保存失败')
                    finish()
                  }
                )
              }

              if (!dirEntry || !dirEntry.getFile) {
                doCopy()
                return
              }

              dirEntry.getFile(
                destName,
                { create: false },
                (existEntry) => {
                  // 文件存在：先删除再覆盖
                  existEntry.remove(
                    () => doCopy(),
                    () => doCopy() // 删除失败也继续尝试（可能是目录/权限问题）
                  )
                },
                () => doCopy() // 不存在：直接复制
              )
            }

            // _downloads：使用 requestFileSystem 获取目录对象（更兼容 Android 10+）
            if (destDirUrl === '_downloads/') {
              window.plus.io.requestFileSystem(
                window.plus.io.PUBLIC_DOWNLOADS,
                (fs) => {
                  fs.root.getDirectory(
                    'khgl',
                    { create: true, exclusive: false },
                    (khglDir) => doCopyToDir(khglDir),
                    () => doCopyToDir(fs.root)
                  )
                },
                (err) => {
                  console.error('无法访问 _downloads:', err)
                  this._toast('无法访问下载目录')
                  finish()
                }
              )
              return
            }

            // 其他目录按原逻辑解析
            window.plus.io.resolveLocalFileSystemURL(
              destDirUrl,
              (dirEntry) => doCopyToDir(dirEntry),
              (err) => {
                console.error('无法访问目标目录:', err, destDirUrl)
                this._toast('无法访问保存目录')
                finish()
              }
            )
          },
          (err) => {
            console.error('无法访问临时文件:', err, srcPath)
            this._toast('导出文件不存在')
            this.exportSaving = false
          }
        )
      } catch (e) {
        console.error('保存失败:', e)
        this._toast('保存失败')
        this.exportSaving = false
      }
    },

    copyExportPath() {
      const text = this.exportSavedPath || ''
      if (!text) return

      // 优先 clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this._toast('已复制路径')
        }).catch(() => {
          this._fallbackCopyText(text)
        })
        return
      }
      this._fallbackCopyText(text)
    },

    _fallbackCopyText(text) {
      try {
        const el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        this._toast('已复制路径')
      } catch (e) {
        this._toast('复制失败，请长按路径手动复制')
      }
    }
  }
}
</script>

<style scoped>
.customer-detail-container {
  width: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  padding: 10px 4px 4px;
}

.page-title-block h2 {
  margin: 0;
  color: #0f8f59;
  font-size: 22px;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #80848f;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.mobile-actions {
  flex-direction: column;
  align-items: stretch;
}

.mobile-actions .el-button {
  margin-bottom: 10px;
  width: 100%;
}

.customer-info-card {
  margin-bottom: 20px;
  border-radius: 14px;
  box-shadow: 0 14px 32px rgba(15, 143, 89, 0.08);
}

.customer-info-card h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.customer-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.mobile-info {
  grid-template-columns: 1fr !important;
  gap: 10px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-item label {
  width: 100px;
  font-weight: bold;
  color: #606266;
}

.mobile-info .info-item {
  flex-direction: column;
  align-items: flex-start;
}

.mobile-info .info-item label {
  width: 100%;
  margin-bottom: 5px;
}

.info-item span {
  color: #333;
}

.address-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-btn-inline {
  margin-left: 8px;
  padding: 0 4px;
}

.location-section h4 {
  margin-top: 20px;
  margin-bottom: 10px;
  color: #606266;
}

.map-container {
  height: 200px;
  margin-bottom: 10px;
  position: relative;
}

.map {
  width: 100%;
  height: 100%;
}

.map-nav-float {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  background-color: rgba(15, 143, 89, 0.92);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
}

.map-nav-float i {
  font-size: 14px;
}

.no-location {
  color: #909399;
  padding: 10px;
  background-color: #f5f7fa;
  text-align: center;
}

.detail-info-window-content {
  padding: 10px;
  max-width: 260px;
}

.detail-info-window-content h3 {
  margin-top: 0;
  margin-bottom: 10px;
}

.detail-info-window-content p {
  margin-bottom: 5px;
}

.detail-info-window-actions {
  margin-top: 8px;
}

.visit-records-card h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.visit-records-card {
  border-radius: 14px;
  box-shadow: 0 14px 32px rgba(15, 143, 89, 0.04);
}

/* 移动端回访记录卡片样式 */
.visit-records-mobile {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.visit-record-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #ebeef5;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.record-id {
  font-weight: bold;
  color: #606266;
}

.record-actions {
  display: flex;
  gap: 8px;
}

.record-content p {
  margin: 8px 0;
  padding: 0;
  line-height: 1.5;
}

.record-content strong {
  color: #606266;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .customer-detail-container {
    padding: 0 10px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .page-header h2 {
    font-size: 18px;
  }
  
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-actions .el-button {
    margin-bottom: 10px;
    width: 100%;
  }
  
  .customer-info {
    grid-template-columns: 1fr !important;
    gap: 10px;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .info-item label {
    width: 100%;
    margin-bottom: 5px;
  }
  
  .map-container {
    height: 180px;
  }
  
  .visit-record-card {
    padding: 12px;
  }
  
  .record-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .record-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>